/**
 * Magig analytics — typed access to the global the inline stub installs in
 * index.html. The stub queues calls made before mag.js lands, so these are
 * safe to fire at any point in the app's life; the optional chaining only
 * covers the case where the script is blocked outright.
 *
 * Distinct from `src/lib/analytics` (our own product_event pipeline): two
 * consumers, two different readers — do not collapse them.
 *
 * There is deliberately no identify() wrapper, and no signup_completed: both
 * belong to the account rather than the page, and the browser cannot even see
 * a Google signup complete (the OAuth return runs no SDK). The backend owns
 * that pair — see MagigService. What stays here is what only the browser knows.
 */
type MagWindow = Window & {
  mag?: {
    track: (event: string, properties?: Record<string, unknown>) => void;
    identify: (id: string, traits?: Record<string, unknown>) => void;
    flush?: () => Promise<void>;
  };
};

/**
 * Fires `register_started` when the register page opens — the funnel's top,
 * against which the backend's `signup_completed` is the completion rate.
 *
 * Deliberately NOT guarded once-per-browser: a visitor who opens /register,
 * leaves and comes back has started twice, and collapsing that would hide the
 * repeat consideration the funnel exists to show. The StrictMode double-mount
 * guard lives at the call site.
 */
export function trackRegisterStarted(): void {
  trackConversion("register_started");
}

export function trackConversion(
  name: string,
  props?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  (window as MagWindow).mag?.track(name, props);
}

/** Whether the SDK was loaded on this page — see index.html for when it is. */
export function isMagLoaded(): boolean {
  return typeof window !== "undefined" && (window as MagWindow).mag !== undefined;
}

/**
 * Sends anything still queued. The SDK batches on a 5s timer, so a signup
 * tracked immediately before a full page load would otherwise be lost.
 */
export function flushMag(): Promise<void> {
  const mag = typeof window === "undefined" ? undefined : (window as MagWindow).mag;
  return mag?.flush?.() ?? Promise.resolve();
}
